/**
 * @fileoverview Main application layout component providing responsive navigation
 * with collapsible sidebar, mobile/tablet/desktop breakpoints, and customisable
 * header slots.
 */

import * as React from 'react';
import { SxProps } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import type {} from '@mui/material/themeCssVarsAugmentation';
import { NavigationContext, WindowContext } from '../../contexts';
import { AppHeader, type AppHeaderProps } from './AppHeader';
import { SidebarNavList } from './SidebarNavList';
import { SidebarExpansionContext, type SidebarExpansion } from './SidebarExpansionContext';
import { createDrawerConfigs, UnifiedDrawer } from './DrawerConfig';
import { useNavigationDrawerState } from './hooks/useNavigationDrawerState';
import { getDrawerSxTransitionMixin, getDrawerWidthTransitionMixin } from './utils';

export interface SidebarFooterProps {}

export interface AppLayoutSlotProps {
  header?: Omit<AppHeaderProps, 'menuOpen' | 'onToggleMenu' | 'hideMenuButton'>;
  sidebarFooter?: SidebarFooterProps;
}

export interface AppLayoutSlots {
  header?: React.JSXElementConstructor<AppHeaderProps>;
  sidebarFooter?: React.JSXElementConstructor<SidebarFooterProps>;
}

export interface AppLayoutProps {
  children: React.ReactNode;
  defaultSidebarCollapsed?: boolean;
  disableCollapsibleSidebar?: boolean;
  hideNavigation?: boolean;
  sidebarExpandedWidth?: number | string;
  slots?: AppLayoutSlots;
  slotProps?: AppLayoutSlotProps;
  sx?: SxProps;
}

function AppLayout(props: AppLayoutProps): React.JSX.Element {
  const {
    children,
    defaultSidebarCollapsed = false,
    disableCollapsibleSidebar = false,
    hideNavigation = false,
    sidebarExpandedWidth = 240,
    slots,
    slotProps,
    sx,
  } = props;

  const navigation = React.useContext(NavigationContext);
  const appWindowContext = React.useContext(WindowContext);

  // Lifted sidebar expansion state, shared with the depth-0 `SidebarNavList` and
  // the app bar's "Collapse all" button via context.
  const [expandedItemIds, setExpandedItemIds] = React.useState<string[]>([]);
  const collapseAll = React.useCallback(() => setExpandedItemIds([]), []);
  const sidebarExpansion = React.useMemo<SidebarExpansion>(
    () => ({
      expandedItemIds,
      setExpandedItemIds,
      collapseAll,
      anyExpanded: expandedItemIds.length > 0,
    }),
    [expandedItemIds, collapseAll],
  );

  const {
    isExpanded: isNavigationExpanded,
    isFullyExpanded: isNavigationFullyExpanded,
    isMobileExpanded: isMobileNavigationExpanded,
    isOverMdViewport,
    hasTransitions: hasDrawerTransitions,
    isDesktopCollapsed,
    isMobileCollapsed,
    setExpanded: setIsNavigationExpanded,
    closeMobile: handleNavigationLinkClick,
  } = useNavigationDrawerState({
    defaultCollapsed: defaultSidebarCollapsed,
    disableCollapsible: disableCollapsibleSidebar,
    appWindow: appWindowContext,
  });

  const handleSetNavigationExpanded = React.useCallback(
    (newExpanded: boolean) => () => {
      setIsNavigationExpanded(newExpanded);
    },
    [setIsNavigationExpanded],
  );

  const handleToggleHeaderMenu = React.useCallback(
    (isExpanded: boolean) => {
      setIsNavigationExpanded(isExpanded);
    },
    [setIsNavigationExpanded],
  );

  const SidebarFooterSlot = slots?.sidebarFooter ?? null;
  const HeaderSlot = slots?.header ?? AppHeader;

  const headerSlotProps: AppHeaderProps = React.useMemo(
    () => ({
      menuOpen: isNavigationExpanded,
      onToggleMenu: handleToggleHeaderMenu,
      hideMenuButton: hideNavigation || (isOverMdViewport && disableCollapsibleSidebar),
      ...slotProps?.header,
    }),
    [
      isNavigationExpanded,
      handleToggleHeaderMenu,
      hideNavigation,
      isOverMdViewport,
      disableCollapsibleSidebar,
      slotProps,
    ],
  );

  const getDrawerContent = React.useCallback(
    (viewport: 'phone' | 'tablet' | 'desktop') => (
      <React.Fragment>
        <Toolbar />
        <Box
          component="nav"
          aria-label={`${viewport.charAt(0).toUpperCase()}${viewport.slice(1)}`}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflow: 'auto',
            overflowX: 'hidden',
            pt: navigation[0]?.kind === 'header' ? 0 : 2,
            ...(hasDrawerTransitions
              ? getDrawerSxTransitionMixin(isNavigationFullyExpanded, 'padding')
              : {}),
          }}
        >
          <SidebarNavList
            subNavigation={navigation}
            onLinkClick={handleNavigationLinkClick}
            isFullyExpanded={isNavigationFullyExpanded}
            hasDrawerTransitions={hasDrawerTransitions}
            sidebarExpandedWidth={sidebarExpandedWidth}
          />
          {SidebarFooterSlot ? (
            <SidebarFooterSlot {...slotProps?.sidebarFooter} />
          ) : null}
        </Box>
      </React.Fragment>
    ),
    [
      SidebarFooterSlot,
      slotProps?.sidebarFooter,
      handleNavigationLinkClick,
      hasDrawerTransitions,
      isNavigationFullyExpanded,
      navigation,
      sidebarExpandedWidth,
    ],
  );

  const getDrawerSharedSx = React.useCallback(
    (isCollapsed: boolean, isTemporary: boolean) => {
      const drawerWidth = isCollapsed ? 0 : sidebarExpandedWidth;

      return {
        displayPrint: 'none',
        width: drawerWidth,
        flexShrink: 0,
        ...getDrawerWidthTransitionMixin(isNavigationExpanded),
        ...(isTemporary ? { position: 'absolute' } : {}),
        [`& .MuiDrawer-paper`]: {
          // Temporary drawers need position: fixed for anchor to work correctly
          position: isTemporary ? 'fixed' : 'absolute',
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundImage: 'none',
          ...getDrawerWidthTransitionMixin(isNavigationExpanded),
        },
      };
    },
    [isNavigationExpanded, sidebarExpandedWidth],
  );

  const drawerConfigs = React.useMemo(
    () =>
      createDrawerConfigs(
        disableCollapsibleSidebar,
        isMobileNavigationExpanded,
        isMobileCollapsed,
        isDesktopCollapsed,
      ),
    [disableCollapsibleSidebar, isMobileNavigationExpanded, isMobileCollapsed, isDesktopCollapsed],
  );

  return (
    <SidebarExpansionContext.Provider value={sidebarExpansion}>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          overflow: 'hidden',
          height: '100vh',
          width: '100vw',
          ...sx,
        }}
      >
        <HeaderSlot {...headerSlotProps} />
        {!hideNavigation ? (
          <React.Fragment>
            {drawerConfigs.map((config) => (
              <UnifiedDrawer
                key={config.viewport}
                config={{
                  ...config,
                  onClose: handleSetNavigationExpanded(false),
                }}
                drawerSx={{
                  display: config.display,
                  ...getDrawerSharedSx(config.isCollapsed, config.variant === 'temporary'),
                }}
                appWindowDocument={appWindowContext?.document}
              >
                {getDrawerContent(config.viewport)}
              </UnifiedDrawer>
            ))}
          </React.Fragment>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
          }}
        >
          <Toolbar />
          <Box
            component="main"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              overflow: 'auto',
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </SidebarExpansionContext.Provider>
  );
}

export { AppLayout };
